// src/features/quotation/components/QuotationForm.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import {
  DISCOUNT_TYPES,
  CURRENCY_OPTIONS,
  DEFAULT_VALUES,
} from "../types/quotationTypes";
import { calculateTotals, formatCurrency } from "../utils/quotationHelpers";
import QuotationItemForm from "./QuotationItemForm";

const QuotationForm = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      return {
        ...initialData,
        date:
          initialData.date?.split("T")[0] ||
          new Date().toISOString().split("T")[0],
        validUntil:
          initialData.validUntil?.split("T")[0] ||
          new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
      };
    }
    return {
      number: `QUOT-${Date.now().toString().slice(-4)}`,
      title: "",
      description: "",
      clientName: "",
      clientEmail: "",
      clientPhone: "",
      clientCompany: "",
      clientAddress: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
      date: new Date().toISOString().split("T")[0],
      validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      items: DEFAULT_VALUES.items,
      discountType: DISCOUNT_TYPES.PERCENTAGE,
      discount: 0,
      taxRate: 10,
      paymentTerms: "",
      notes: "",
      termsAndConditions: "",
      currency: "USD",
      showTax: true,
      showDiscount: true,
      showNotes: true,
      showTerms: true,
      status: "draft",
    };
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    discountAmount: 0,
    taxAmount: 0,
    total: 0,
  });
  const [errors, setErrors] = useState({});

  // ✅ FIX: Use a separate function that doesn't call itself
  const calculateTotalsHandler = (items, discount, discountType, taxRate) => {
    // Calculate subtotal
    let subtotal = 0;
    items.forEach((item) => {
      const itemTotal = (item.quantity || 0) * (item.unitPrice || 0);
      subtotal += itemTotal;
    });

    // Calculate discount
    let discountAmount = 0;
    if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
      discountAmount = subtotal * (discount / 100);
    } else {
      discountAmount = discount || 0;
    }

    // Calculate tax
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = taxableAmount * ((taxRate || 0) / 100);

    // Calculate total
    const total = subtotal - discountAmount + taxAmount;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  };

  // ✅ Update totals when dependencies change
  useEffect(() => {
    const calculated = calculateTotalsHandler(
      formData.items,
      formData.discount || 0,
      formData.discountType || DISCOUNT_TYPES.PERCENTAGE,
      formData.taxRate || 0,
    );
    setTotals(calculated);
  }, [
    formData.items,
    formData.discount,
    formData.discountType,
    formData.taxRate,
  ]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const handleItemChange = (items) => {
    setFormData((prev) => ({ ...prev, items }));
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      description: "",
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      notes: "",
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title || formData.title.length < 3) {
      newErrors.title = "Title must be at least 3 characters";
    }

    if (!formData.clientName || formData.clientName.length < 2) {
      newErrors.clientName = "Client name is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.clientEmail || !emailRegex.test(formData.clientEmail)) {
      newErrors.clientEmail = "Valid email is required";
    }

    if (!formData.date) {
      newErrors.date = "Date is required";
    }

    if (!formData.validUntil) {
      newErrors.validUntil = "Valid until date is required";
    } else if (new Date(formData.validUntil) < new Date(formData.date)) {
      newErrors.validUntil = "Valid until must be after date";
    }

    if (!formData.items || formData.items.length === 0) {
      newErrors.items = "At least one item is required";
    } else {
      formData.items.forEach((item, index) => {
        if (!item.description || item.description.length < 2) {
          newErrors[`items[${index}].description`] = "Description is required";
        }
        if (!item.quantity || item.quantity < 1) {
          newErrors[`items[${index}].quantity`] = "Quantity must be at least 1";
        }
        if (!item.unitPrice || item.unitPrice < 0) {
          newErrors[`items[${index}].unitPrice`] = "Unit price is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      ...totals,
      items: formData.items.map((item) => ({
        ...item,
        total: (item.quantity || 0) * (item.unitPrice || 0),
      })),
    };

    onSubmit && onSubmit(submitData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            {initialData ? "Edit Quotation" : "Create New Quotation"}
          </Typography>
          <Divider sx={{ mt: 2 }} />
        </Grid>

        {/* Basic Info */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Quotation Number"
            value={formData.number}
            disabled
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status || "draft"}
              onChange={(e) => handleChange("status", e.target.value)}
              label="Status"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title"
            value={formData.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
            required
            size="small"
            error={!!errors.title}
            helperText={errors.title}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            multiline
            rows={2}
            size="small"
          />
        </Grid>

        {/* Client Information */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.secondary"
          >
            Client Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Client Name"
            value={formData.clientName || ""}
            onChange={(e) => handleChange("clientName", e.target.value)}
            required
            size="small"
            error={!!errors.clientName}
            helperText={errors.clientName}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Client Email"
            type="email"
            value={formData.clientEmail || ""}
            onChange={(e) => handleChange("clientEmail", e.target.value)}
            required
            size="small"
            error={!!errors.clientEmail}
            helperText={errors.clientEmail}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Client Phone"
            value={formData.clientPhone || ""}
            onChange={(e) => handleChange("clientPhone", e.target.value)}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Client Company"
            value={formData.clientCompany || ""}
            onChange={(e) => handleChange("clientCompany", e.target.value)}
            size="small"
          />
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            value={formData.clientAddress?.street || ""}
            onChange={(e) =>
              handleNestedChange("clientAddress", "street", e.target.value)
            }
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="City"
            value={formData.clientAddress?.city || ""}
            onChange={(e) =>
              handleNestedChange("clientAddress", "city", e.target.value)
            }
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="State"
            value={formData.clientAddress?.state || ""}
            onChange={(e) =>
              handleNestedChange("clientAddress", "state", e.target.value)
            }
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Zip Code"
            value={formData.clientAddress?.zipCode || ""}
            onChange={(e) =>
              handleNestedChange("clientAddress", "zipCode", e.target.value)
            }
            size="small"
          />
        </Grid>

        {/* Dates */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.secondary"
          >
            Dates
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={formData.date || ""}
            onChange={(e) => handleChange("date", e.target.value)}
            required
            size="small"
            InputLabelProps={{ shrink: true }}
            error={!!errors.date}
            helperText={errors.date}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Valid Until"
            type="date"
            value={formData.validUntil || ""}
            onChange={(e) => handleChange("validUntil", e.target.value)}
            required
            size="small"
            InputLabelProps={{ shrink: true }}
            error={!!errors.validUntil}
            helperText={errors.validUntil}
          />
        </Grid>

        {/* Items */}
        <Grid item xs={12}>
          <QuotationItemForm
            items={formData.items}
            onItemChange={handleItemChange}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
          />
          {errors.items && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.items}
            </Alert>
          )}
        </Grid>

        {/* Financials */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={formData.discountType || DISCOUNT_TYPES.PERCENTAGE}
                    onChange={(e) =>
                      handleChange("discountType", e.target.value)
                    }
                    label="Discount Type"
                  >
                    <MenuItem value={DISCOUNT_TYPES.PERCENTAGE}>
                      Percentage
                    </MenuItem>
                    <MenuItem value={DISCOUNT_TYPES.FIXED}>
                      Fixed Amount
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Discount"
                  type="number"
                  value={formData.discount || 0}
                  onChange={(e) =>
                    handleChange("discount", parseFloat(e.target.value) || 0)
                  }
                  size="small"
                  InputProps={{
                    startAdornment: formData.discountType ===
                      DISCOUNT_TYPES.FIXED && <span>$</span>,
                    endAdornment: formData.discountType ===
                      DISCOUNT_TYPES.PERCENTAGE && <span>%</span>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tax Rate"
                  type="number"
                  value={formData.taxRate || 0}
                  onChange={(e) =>
                    handleChange("taxRate", parseFloat(e.target.value) || 0)
                  }
                  size="small"
                  InputProps={{
                    endAdornment: <span>%</span>,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Totals */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: "#e8f5e9" }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(totals.subtotal || 0, formData.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Discount:
                </Typography>
                <Typography variant="body1" color="error" fontWeight="bold">
                  -
                  {formatCurrency(
                    totals.discountAmount || 0,
                    formData.currency,
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Tax:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(totals.taxAmount || 0, formData.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Total:
                </Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {formatCurrency(totals.total || 0, formData.currency)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Additional Info */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.secondary"
          >
            Additional Information
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Payment Terms"
            value={formData.paymentTerms || ""}
            onChange={(e) => handleChange("paymentTerms", e.target.value)}
            size="small"
            placeholder="e.g., Net 30, 50% upfront"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            value={formData.notes || ""}
            onChange={(e) => handleChange("notes", e.target.value)}
            multiline
            rows={2}
            size="small"
            placeholder="Additional notes about the quotation"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Terms & Conditions"
            value={formData.termsAndConditions || ""}
            onChange={(e) => handleChange("termsAndConditions", e.target.value)}
            multiline
            rows={2}
            size="small"
            placeholder="Terms and conditions for this quotation"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a67d8 0%, #6b46a1 100%)",
                },
              }}
            >
              {initialData ? "Update Quotation" : "Create Quotation"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuotationForm;
